import React, { useState } from 'react';
import { Tag } from 'antd';
import { useHistory } from 'react-router-dom';

import { SearchBy } from 'src/component/Table/enums/searchby.enum';
import Loader from 'src/component/Loader/Loader';
import { formatDate } from 'src/helpers/millisecondsToFormattedDate';
import { useGetCompanies, CompanyRow } from 'src/reactQuery/companies/useGetCompanies';
import NotVerified from '../Suppliers/svgs/NotVerified';
import Verified from '../Suppliers/svgs/Verified';
import NoxTable from '../../component/Table';
import InnerHeader from '../../component/InnerHeader';
import Copy from '../../component/Copy';

interface CompanyTableRow {
  companyId: string;
  email: string;
  fullName: string;
  isVerified: boolean;
  key: string;
  phoneNumber: string;
  registrationDate?: string;
  userId: string;
}

const Companies = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const history = useHistory();
  const { data, isLoading } = useGetCompanies();

  const openDetail = (id: string) => {
    history.push({ pathname: `/authentication/customers/${id}` });
  };

  const rows: CompanyTableRow[] = (data?.data ?? []).map(({ user, profile }: CompanyRow) => ({
    companyId: user.metadata?.companyId || user.idNumber || '-',
    email: user.email || '-',
    fullName:
      user.metadata?.companyName ||
      user.fullName ||
      `${user.firstName || ''} ${user.lastName || ''}`.trim(),
    isVerified: profile?.type === 'COMPANY' && profile?.verified === true,
    key: user._id,
    phoneNumber: user.phone || '-',
    registrationDate: user.created,
    userId: user._id
  }));

  const columns = [
    {
      dataIndex: 'companyId',
      key: 'companyId',
      render: (id: string) => {
        return <Copy text={id} />;
      },
      sorter: (a: CompanyTableRow, b: CompanyTableRow) => a.companyId.localeCompare(b.companyId),
      title: 'Company ID'
    },
    {
      dataIndex: 'fullName',
      key: 'fullName',
      render: (fullName: string) => {
        return <Copy text={fullName} />;
      },
      sorter: (a: CompanyTableRow, b: CompanyTableRow) => a.fullName.localeCompare(b.fullName),
      title: 'Company Name'
    },
    {
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => {
        return <Copy text={email} />;
      },
      sorter: (a: CompanyTableRow, b: CompanyTableRow) => a.email.localeCompare(b.email),
      title: 'Email'
    },
    {
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (phoneNumber: string) => {
        return <Copy text={phoneNumber} />;
      },
      sorter: (a: CompanyTableRow, b: CompanyTableRow) =>
        a.phoneNumber.localeCompare(b.phoneNumber),
      title: 'Phone Number'
    },
    {
      dataIndex: 'registrationDate',
      key: 'registrationDate',
      render: (registrationDate?: string) => {
        return (
          <Copy text={registrationDate ? formatDate(new Date(registrationDate))?.[0] || '' : '-'} />
        );
      },
      sorter: (a: CompanyTableRow, b: CompanyTableRow) =>
        (a.registrationDate || '').localeCompare(b.registrationDate || ''),
      title: 'Registration date'
    },
    {
      dataIndex: 'isVerified',
      key: 'isVerified',
      render: (isVerified: boolean) => {
        if (isVerified) {
          return <Verified />;
        }

        return <NotVerified />;
      },
      sorter: (a: CompanyTableRow, b: CompanyTableRow) =>
        Number(a.isVerified) - Number(b.isVerified),
      title: 'Status'
    },
    {
      key: 'edit',
      render: (row: CompanyTableRow) => {
        return <Tag onClick={() => openDetail(row.userId)}>View</Tag>;
      }
    }
  ];

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div
      style={{
        height: 'calc(100vh - 165px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={data?.paging.total} title="Companies" />
      <NoxTable
        columns={columns}
        data={rows}
        searchBy={SearchBy.FULL_NAME}
        // @ts-ignore
        selectedRowKeys={selectedRowKeys}
        // @ts-ignore
        setSelectedRowKeys={setSelectedRowKeys}
      />
    </div>
  );
};

export default Companies;
